import { CheckOutlined } from '@ant-design/icons';
import { MQ } from '@common/constants/mq.constant';
import { THEME } from '@common/constants/theme.constant';
import { Icon } from '@common/general-ui/icon/icon.component';
import { Link } from '@common/general-ui/link/link.component';
import { ISerializedStylesList } from '@common/interfaces/serialized-styles-list.interface';
import { ILinkItem } from '@common/utils/make-links/make-links.util';
import { Button as AntButton } from 'antd';
import React, { ReactNode } from 'react';
import { css } from 'twin.macro';

interface IProps {
    name: string;
    variant:
        | 'empty'
        | 'default'
        | 'default-outlined'
        | 'orange'
        | 'orange-outline'
        | 'orange-transparent'
        | 'orange-dark-hover'
        | 'green'
        | 'green-outline'
        | 'black-outline'
        | 'gray-outline';
    size?: 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge';
    isValid?: boolean;
    disabled?: boolean;
    onClick?: () => void;
    external?: boolean;
    children?: ReactNode;
    type?: 'submit' | 'button';
    className?: string;
    href?: ILinkItem;
    target?: string;
    icon?: React.ReactNode;
}

// eslint-disable-next-line react/display-name
export const Button = React.forwardRef<HTMLDivElement, IProps>(
    (
        {
            name,
            type = 'submit',
            icon,
            size = 'medium',
            children,
            external,
            target,
            disabled,
            className,
            variant,
            href,
            isValid,
            ...rest
        },
        _ref,
    ) => {
        return (
            <AntButton
                name={name}
                {...rest}
                disabled={disabled}
                css={styles.button({ disabled, variant, size })}
                className={className}
                data-testid={`button-${name}`}
                htmlType={type}
                ref={_ref}
            >
                {href ? (
                    <Link name={`link-${name}`} href={href} noStyle={true} tw="flex" target={target}>
                        {icon}
                        {children && <span css={styles.children}> {children}</span>}
                    </Link>
                ) : (
                    <>
                        {isValid && <Icon icon={CheckOutlined} />}
                        {icon}
                        {children && <span css={styles.children}> {children}</span>}
                    </>
                )}
            </AntButton>
        );
    },
);

// TODO: remove unused styles (variants, size, etc)
const styles = {
    button: ({ disabled, variant, size }: Pick<IProps, 'disabled' | 'variant' | 'size'>): ISerializedStylesList => [
        css`
            height: min-content;
            line-height: initial;
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            font-weight: 600;
            border-width: 0;
            outline: none !important;
            transition: all 0.3s ease-in-out;
            cursor: pointer;
            border-radius: 4px;
            text-transform: uppercase;
            letter-spacing: 1.5px;

            span:first-of-type {
                display: flex;
                justify-content: center;
                align-items: center;
            }
            .anticon {
                display: flex;
                justify-content: center;
                align-items: center;
            }
        `,
        disabled &&
            css`
                cursor: not-allowed;
                filter: alpha(opacity=65);
                background-color: #cfcfcf;
                box-shadow: none;
                opacity: 0.65;
            `,
        variant === 'empty' &&
            css`
                text-transform: none;
                letter-spacing: 0px;
                font-weight: normal;
                border: 0;
                background-color: transparent;
                box-shadow: none;
                :hover,
                :active,
                :focus {
                    border: 0;
                    background-color: transparent;
                }
            `,
        variant === 'default' &&
            css`
                background-color: #cfcfcf;
                color: black;

                :hover,
                :focus {
                    background-color: ${THEME.GRAY};
                    outline-color: transparent;
                    outline-style: none;
                    ${THEME.BOX_SHADOW_GRAY}
                }
            `,
        variant === 'default-outlined' &&
            css`
                border: 1px solid ${THEME.GRAY};
                color: black;

                :hover,
                :focus {
                    background-color: ${THEME.GRAY};
                    outline-color: transparent;
                    outline-style: none;
                    ${THEME.BOX_SHADOW_GREEN}
                }
            `,
        variant === 'orange' &&
            css`
                border: 1px solid ${THEME.ORANGE};
                background-color: ${THEME.ORANGE};
                color: ${THEME.BLACK};

                :focus,
                :hover {
                    border: 1px solid ${THEME.ORANGE};
                    background-color: white;
                    outline-color: transparent;
                    outline-style: none;
                    color: ${THEME.BLACK};
                }
            `,
        variant === 'orange-dark-hover' &&
            css`
                border: 1px solid ${THEME.ORANGE};
                background-color: ${THEME.ORANGE};
                color: ${THEME.BLACK};

                :focus,
                :hover {
                    border: 1px solid #e39e14;
                    background-color: #e39e14;
                    outline-color: transparent;
                    outline-style: none;
                    color: ${THEME.BLACK};
                }
            `,
        variant === 'orange-transparent' &&
            css`
                border: 1px solid ${THEME.ORANGE};
                background-color: ${THEME.ORANGE};
                color: ${THEME.BLACK};

                :focus {
                    border: 1px solid ${THEME.ORANGE};
                    background-color: ${THEME.ORANGE};
                    color: ${THEME.BLACK};
                }

                :hover {
                    border: 1px solid ${THEME.ORANGE};
                    background-color: transparent;
                    outline-color: transparent;
                    outline-style: none;
                    color: ${THEME.BLACK};
                }
            `,
        variant === 'orange-outline' &&
            css`
                border: 1px solid ${THEME.ORANGE};
                background-color: white;
                color: black;

                :hover,
                :focus {
                    border: 1px solid ${THEME.ORANGE};
                    background-color: ${THEME.ORANGE};
                    outline-color: transparent;
                    outline-style: none;
                    color: ${THEME.WHITE};
                }
            `,

        variant === 'green' &&
            css`
                background-color: ${THEME.GREEN};
                color: white;

                :hover,
                :focus {
                    background-color: ${THEME.GREEN};
                    color: white;
                    outline-color: transparent;
                    outline-style: none;
                    ${THEME.BOX_SHADOW_GREEN}
                }
            `,
        variant === 'green-outline' &&
            css`
                border: 1px solid ${THEME.GREEN};
                background-color: ${THEME.WHITE};
                color: #000;

                :hover,
                :focus {
                    border: 1px solid ${THEME.GREEN};
                    outline-color: transparent;
                    outline-style: none;
                    color: ${THEME.BLACK};
                    ${THEME.BOX_SHADOW_GREEN}
                }
            `,
        variant === 'black-outline' &&
            css`
                border: 1px solid ${THEME.BLACK};
                background-color: transparent;
                color: ${THEME.BLACK};

                :focus {
                    border: 1px solid ${THEME.BLACK};
                    background-color: transparent;
                    outline-color: transparent;
                    outline-style: none;
                    color: ${THEME.BLACK};
                }

                :hover {
                    border: 1px solid ${THEME.BLACK};
                    background-color: ${THEME.BLACK};
                    outline-color: transparent;
                    outline-style: none;
                    color: ${THEME.WHITE};
                }
            `,

        // This type of button have complete different base styles!
        variant === 'gray-outline' &&
            css`
                letter-spacing: 0px;
                font-size: 15px;
                text-transform: none;
                font-weight: 400;
                border-radius: 6px;
                width: auto;
                border: 1px solid ${THEME.GRAY_300};
                background-color: transparent;
                color: ${THEME.BLACK};

                :focus {
                    border: 1px solid ${THEME.GRAY_300};
                    background-color: transparent;
                    outline-color: transparent;
                    outline-style: none;
                    color: ${THEME.BLACK};
                }

                :hover {
                    border: 1px solid ${THEME.BLACK};
                    outline-color: transparent;
                    outline-style: none;
                    color: ${THEME.BLACK};
                }
            `,

        size === 'small' &&
            css`
                font-size: ${THEME.SMALL_FONT_SIZE};
                padding: 6px 12px;
                // height: 30px;

                * {
                    font-size: ${THEME.SMALL_FONT_SIZE};
                }
            `,
        size === 'medium' &&
            css`
                height: 34px;
                padding: 7px 12px;
                font-size: ${THEME.BASE_FONT_SIZE};
            `,
        size === 'large' &&
            css`
                // height: 40px;
                padding: 9px 12px;
                font-size: 20px;
            `,

        size === 'xlarge' &&
            css`
                padding: 9px 14px;
                font-size: 15px;

                ${MQ.lg} {
                    padding: 15px 14px;
                }
            `,

        size === 'xxlarge' &&
            css`
                padding: 14px;
                font-size: 15px;
                height: 48px;
            `,
    ],
    children: css`
        display: flex;
        justify-content: center;
        align-items: center;
        padding-top: 2px;
        padding-left: 6px;
    `,
};
